import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Platform, StyleSheet, Text, View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { useDispatch, useSelector } from 'react-redux';

import { RootState } from '../../../store';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { lobbyRoomsRequest, myTablesRequest } from '../../../store/lobby/lobbyActions';
import { selectTotalActiveUsersCount } from '../../../store/lobby/lobbySagas';
import { formatBigNumbers } from '../../../utils/helpers/numberFormat';
import { useDevice } from '../../../utils/hooks/useDevice';
import { RootStackParamList } from '../../navigator';
import { CarouselNav } from '../common/CarouselNav';
import { RecentTablesBar } from '../common/RecentTablesBar';
import { IndicatorScrollView } from '../common/ScrollIndicator';
import { LobbyQuickCards } from '../Lobby/LobbyQuickCards';
import { LobbyDesktopRooms } from './LobbyDesktopRooms';
import { LobbyRoomCarousel, LobbyRoomCarouselHandle } from './LobbyRoomCarousel';

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const SETTLE_MS = 500;

const NAV_BLOCK_HEIGHT = 34;
const NAV_TOP_GAP = 12;

const DESIGN_FRAME_WIDTH = 390;
const DESIGN_CARD_WIDTH = 226;
const DESIGN_CARD_HEIGHT = 416;
const CARD_ASPECT = DESIGN_CARD_HEIGHT / DESIGN_CARD_WIDTH;

// Blocks the design stacks under the carousel nav (Figma iPhone 13 & 14 - 14): nav block
// ends at y 644, quick cards 663-759, recent-tables bar 772-826. The design's jackpot bar under
// them (833-871) is left out on purpose: it pushed the stack past the screen and made it scroll.
const DESIGN_EXTRAS_WIDTH = 362;
const QUICK_CARDS_TOP_GAP = 19;
const RECENT_BAR_TOP_GAP = 13;

// The design leaves 12px between the missions / free-chips row and the card's top edge; the
// row itself floats over the content, hence the overhang on top of that.
const CAROUSEL_TOP_GAP = 12;

type LobbyNavigationProp = NativeStackNavigationProp<RootStackParamList, 'Lobby'>;
type LobbyRoute = RouteProp<RootStackParamList, 'Lobby'>;

export const LobbyRoomSection = () => {
	const navigation = useNavigation<LobbyNavigationProp>();
	const { params } = useRoute<LobbyRoute>();
	const { t } = useTranslation();
	const carouselRef = useRef<LobbyRoomCarouselHandle>(null);
	const initialPage = params?.lobbyPage ?? 0;
	const activeIndexRef = useRef(initialPage);
	const [activeIndex, setActiveIndex] = useState(initialPage);
	const [dotIndex, setDotIndex] = useState(initialPage);
	const carouselProgress = useSharedValue(initialPage);
	const { safeAreaWidth: width, safeAreaHeight: height, insets, isXs, isDesktop } = useDevice();
	const dispatch = useDispatch();
	const {
		items: rooms,
		vipRoom,
		loading,
		loaded,
		lastFetched,
	} = useSelector((s: RootState) => s.lobby);
	const playerLevel = useSelector((s: RootState) => s.auth.user?.level);
	const isLogged = useSelector(selectIsLogged);
	const activeUsersCount = useSelector(selectTotalActiveUsersCount);

	const topReserve = height * 0.1 + insets.top;
	const bottomReserve = (isXs ? width * 0.2 : height * 0.18) + insets.bottom;
	// Only Home hangs the missions / free-chips pills under the header, so the carousel just
	// keeps its own gap below the header row.
	const headerClearance = CAROUSEL_TOP_GAP;
	const availableHeight = Math.max(
		height - topReserve - bottomReserve - headerClearance,
		height * 0.4,
	);
	const navReserveScale = clamp(width / DESIGN_FRAME_WIDTH, 1, 1.6);
	const navHeight = (NAV_BLOCK_HEIGHT + NAV_TOP_GAP) * navReserveScale;
	const widthBudget = width * (DESIGN_CARD_WIDTH / DESIGN_FRAME_WIDTH);

	// The design's stack is taller than a phone screen, so the room card keeps the design's
	// proportions - a 226-wide card in a 390-wide frame - and the page scrolls instead. Only a
	// viewport too short for that card at all shrinks it.
	const carouselBudget = availableHeight - navHeight;
	const cardWidth = Math.min(widthBudget, (carouselBudget * 0.94) / CARD_ASPECT);
	const cardHeight = cardWidth * CARD_ASPECT;
	// The blocks under the nav are sized off the card, the way the design pairs a 362-wide row
	// with a 226-wide card.
	const extrasWidth = cardWidth * (DESIGN_EXTRAS_WIDTH / DESIGN_CARD_WIDTH);
	const extrasScale = extrasWidth / DESIGN_EXTRAS_WIDTH;
	// Hug the card rather than filling the budget, so the nav and everything under it follow
	// immediately instead of after a band of empty space.
	const sectionHeight = Math.min(cardHeight / 0.94, Math.max(carouselBudget, cardHeight));
	const navScale = cardWidth / DESIGN_CARD_WIDTH;

	const navOffset = NAV_TOP_GAP * navScale - (sectionHeight - cardHeight) / 2;
	const layoutKey = `${Math.round(width)}-${Math.round(sectionHeight)}`;

	// Only write to navigation params when the page actually changed: every
	// setParams mutates navigation state and re-renders all its subscribers,
	// which is wasteful during swipe gestures and on mount.
	const lastPersistedPageRef = useRef<number>(initialPage);
	const persistPage = useCallback(
		(pageIndex: number) => {
			if (lastPersistedPageRef.current === pageIndex) return;
			lastPersistedPageRef.current = pageIndex;
			navigation.setParams({ lobbyPage: pageIndex });
		},
		[navigation],
	);

	const visibleRooms = useMemo(
		() =>
			rooms.filter(room => {
				return !(
					typeof playerLevel === 'number' &&
					Number.isFinite(playerLevel) &&
					typeof room.maxLevel === 'number' &&
					Number.isFinite(room.maxLevel) &&
					playerLevel > room.maxLevel
				);
			}),
		[playerLevel, rooms],
	);
	const visibleRoomsCount = visibleRooms.length;

	useEffect(() => {
		const CACHE_TTL = 5 * 60 * 1000;
		const isCacheValid =
			loaded && lastFetched !== null && Date.now() - lastFetched < CACHE_TTL && rooms.length > 0;

		if (!isCacheValid) {
			dispatch(lobbyRoomsRequest({ start: 0, limit: 20 }));
		}
	}, [dispatch, loaded, lastFetched, rooms.length]);

	useEffect(() => {
		if (isLogged) {
			dispatch(myTablesRequest());
		}
	}, [dispatch, isLogged]);

	useEffect(() => {
		activeIndexRef.current = activeIndex;
		persistPage(activeIndex);
	}, [activeIndex, persistPage]);

	useEffect(() => {
		const safeIndex = clamp(activeIndex, 0, Math.max(0, visibleRoomsCount - 1));

		if (safeIndex === activeIndex) return;

		activeIndexRef.current = safeIndex;
		setActiveIndex(safeIndex);
		setDotIndex(safeIndex);
		persistPage(safeIndex);
		carouselRef.current?.scrollToIndex(safeIndex);
	}, [activeIndex, persistPage, visibleRoomsCount]);

	// onSnapToItem lands while the settle animation is still running, and both
	// setActiveIndex and the setParams inside persistPage re-render every
	// navigation subscriber - measured as a single ~59ms frame right at the end
	// of each swipe. The carousel owns its own position, so nothing here is
	// needed until the motion has stopped; the ref is updated immediately for
	// anything reading it synchronously.
	const commitIndexTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const handleActiveIndexChange = useCallback(
		(nextIndex: number) => {
			activeIndexRef.current = nextIndex;
			setDotIndex(nextIndex);

			if (commitIndexTimerRef.current) clearTimeout(commitIndexTimerRef.current);
			commitIndexTimerRef.current = setTimeout(() => {
				setActiveIndex(nextIndex);
				persistPage(nextIndex);
			}, SETTLE_MS);
		},
		[persistPage],
	);

	useEffect(
		() => () => {
			if (commitIndexTimerRef.current) clearTimeout(commitIndexTimerRef.current);
		},
		[],
	);

	const handleDotPress = useCallback((index: number) => {
		setDotIndex(index);
		carouselRef.current?.scrollToIndex(index);
	}, []);

	const handleStep = useCallback(
		(direction: -1 | 1) => {
			if (visibleRoomsCount <= 1) return;

			const stepped = activeIndexRef.current + direction;

			setDotIndex(((stepped % visibleRoomsCount) + visibleRoomsCount) % visibleRoomsCount);

			if (direction === 1) carouselRef.current?.next();
			else carouselRef.current?.prev();
		},
		[visibleRoomsCount],
	);
	const shouldShowFullLoader = loading && rooms.length === 0;

	const handleRecentTablesPress = useCallback(() => {
		navigation.navigate(isLogged ? 'MyTables' : 'Login');
	}, [isLogged, navigation]);

	// The design badges both quick cards with a live head count. The VIP room reports its
	// own, tournaments have no per-feature count yet, so the lobby-wide figure stands in.
	const vipPlayersCount = vipRoom?.playersCount ?? 0;

	const renderRooms = () => {
		if (shouldShowFullLoader) {
			return (
				<View style={[styles.centeredFill, { height: sectionHeight }]}>
					<ActivityIndicator size='large' color='#B7C1D1' />
				</View>
			);
		}

		if (visibleRoomsCount === 0) {
			return (
				<View style={[styles.centeredFill, { height: sectionHeight }]}>
					<Text style={styles.emptyRoomsText}>
						{t('T_SITE_LOBBY_NO_ROOMS', { defaultValue: 'No rooms available' })}
					</Text>
				</View>
			);
		}

		return (
			<>
				<View nativeID='rn-spine-root-lobby-rooms' style={styles.sharedCanvasLayer} />
				<LobbyRoomCarousel
					key={layoutKey}
					ref={carouselRef}
					rooms={visibleRooms}
					activeIndex={activeIndex}
					onActiveIndexChange={handleActiveIndexChange}
					width={width}
					height={sectionHeight}
					cardWidth={cardWidth}
					cardHeight={cardHeight}
					progress={carouselProgress}
				/>

				<View style={[styles.nav, { marginTop: navOffset }]}>
					<CarouselNav
						count={visibleRoomsCount}
						activeIndex={dotIndex}
						progress={carouselProgress}
						onDotPress={handleDotPress}
						onStep={handleStep}
						hintLabel={t('T_SITE_LOBBY_SWIPE_HINT', {
							defaultValue: 'Swipe to explore rooms',
						})}
						scale={navScale}
					/>
				</View>
			</>
		);
	};

	// The desktop frame lays the rooms out flat instead of on the swipe wheel.
	if (isDesktop) {
		return <LobbyDesktopRooms rooms={visibleRooms} loading={shouldShowFullLoader} />;
	}

	return (
		<IndicatorScrollView
			style={[styles.scroll, { width, maxHeight: availableHeight + headerClearance }]}
			contentContainerStyle={[styles.container, { paddingTop: headerClearance }]}
			showsVerticalScrollIndicator={false}
			// The rooms scroll up under the header, cleared by padding; so does the bar - 4 in from
			// the screen edge, in the margin the centred column leaves, like the design's full-screen
			// lists.
			indicatorInsets={{ top: headerClearance + 8, right: 4 }}
			reserveGutter={false}
		>
			{renderRooms()}

			<View style={{ marginTop: QUICK_CARDS_TOP_GAP * extrasScale }}>
				<LobbyQuickCards
					width={extrasWidth}
					vip={{
						title: t('T_SITE_VIP', { defaultValue: 'VIP' }),
						meta: 'LEVEL 2 - 60%',
						playersLabel: formatBigNumbers(vipPlayersCount, { fractionDigits: 1 }),
						onPress: () => navigation.navigate('VipRooms'),
					}}
					tournaments={{
						title: t('T_SITE_HOME_TOURNAMENTS', { defaultValue: 'TOURNAMENTS' }),
						meta: 'in 04:12',
						playersLabel: formatBigNumbers(activeUsersCount, { fractionDigits: 1 }),
						onPress: () => navigation.navigate('Tournaments'),
					}}
				/>
			</View>

			<View style={{ marginTop: RECENT_BAR_TOP_GAP * extrasScale }}>
				<RecentTablesBar width={extrasWidth} onPress={handleRecentTablesPress} />
			</View>
		</IndicatorScrollView>
	);
};

const styles = StyleSheet.create({
	scroll: {
		alignSelf: 'center',
		// Native scroll views keep scrolling with their clipping turned off, which lets the room
		// cards' glow spill past the list. On web `overflow: visible` replaces the scroll view's
		// own `overflow-y: auto`, so the list could not scroll and its end ran under the nav - the
		// side cards sit inside the screen's width anyway, so web keeps the scroll view's own.
		...(Platform.OS === 'web' ? null : { overflow: 'visible' }),
	},
	container: {
		position: 'relative',
		alignItems: 'center',
		overflow: 'visible',
	},
	centeredFill: {
		alignSelf: 'stretch',
		alignItems: 'center',
		justifyContent: 'center',
	},
	emptyRoomsText: {
		color: '#fff',
	},
	sharedCanvasLayer: {
		...StyleSheet.absoluteFill,
		zIndex: 0,
		pointerEvents: 'none',
	},
	nav: {
		alignSelf: 'center',
		zIndex: 5,
	},
	watchAdContainer: {
		position: 'absolute',
		bottom: '3%',
		left: '20%',
		right: '20%',
		zIndex: 6,
		alignItems: 'center',
	},
});

export default LobbyRoomSection;
